import React, { forwardRef, useImperativeHandle, useState } from "react";
import CnAdministratorAccess from "./CnAdministratorAccess";
import { Button, Col, Divider, Flex, Form, Input, Modal, Row, Select, Space, Switch, Tooltip, Typography } from "antd";
import { UserOutlined, WarningOutlined } from '@ant-design/icons';
import { formatAdministratorAccess, formatCustomContactSupport } from "../saveCnSoftAccess";

export interface ContactInfo {
    name: string,
    phone: string,
    email: string
}

interface CnGeneralConfigurationProps {
    orgaId: string;
    softaccessId: string;
    msPro: boolean;
    name: string;
    rg3DefaultTab: string;
    customContact: ContactInfo | null;
    adminAccess: string;
}

export interface CnGeneralConfigurationHandle {
    getSaveData: () => {
        name: string;
        ms_pro: boolean;
        rg3_default_tab: string;
        custom_contact_support: string;
        administrator_access_collaborator: string;
    };
}

const RG3_VIEW_OPTIONS = [
    { label: "Carte", value: "map" },
    { label: "Tableau", value: "table" },
    { label: "Mon équipe", value: "atc" },
    { label: "Dashboard", value: "dashboard" },
    { label: "Agenda", value: "agenda" },
];

const CnGeneralConfiguration = forwardRef<CnGeneralConfigurationHandle, CnGeneralConfigurationProps>(function CnGeneralConfiguration(props, ref) {

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
    const [isMsPro, setIsMsPro] = useState(props.msPro);
    const [name, setName] = useState(props.name);
    const [defaultRg3View, setDefaultRg3View] = useState(props.rg3DefaultTab);
    const [customContactSupport, setCustomContactSupport] = useState<ContactInfo | null>(props.customContact);
    const [adminAccess, setAdminAccess] = useState(props.adminAccess);

    useImperativeHandle(ref, () => ({
        getSaveData: () => ({
            name,
            ms_pro: isMsPro,
            rg3_default_tab: defaultRg3View,
            custom_contact_support: formatCustomContactSupport(customContactSupport),
            administrator_access_collaborator: adminAccess
                ? formatAdministratorAccess(adminAccess)
                : '',
        }),
    }), [name, isMsPro, defaultRg3View, customContactSupport, adminAccess]);

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleOk = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/organisations/${props.orgaId}/cn/deleteLogs`, {
                method: 'DELETE',
            });
            const msg = await response.text();
            setFeedbackMessage(msg);
        } catch (error) {
            console.error('Error clearing logs:', error);
            setFeedbackMessage("Une erreur est survenue lors de la suppression des logs.");
        } finally {
            setTimeout(() => {
                setLoading(false);
                setIsModalOpen(false);
                setFeedbackMessage(null);
            }, 2000);
        }
    }

    return (
        <>
            <Form
                layout="horizontal"
                size="small"
                style={{ marginBottom: 0 }}
                labelCol={{ style: { width: 160 } }}
                wrapperCol={{ style: { flex: 1 } }}
                colon={false}
            >
                <Row gutter={[16, 8]}>
                    <Col span={12}>
                        <Form.Item label="Nom" className="no-box-shadow" style={{ marginBottom: 8 }}>
                            <Input value={name} onChange={(e) => setName(e.target.value)} />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <Form.Item label="Vue par défaut du Rg3" className="no-box-shadow" style={{ marginBottom: 8 }}>
                            <Select
                                value={defaultRg3View}
                                onChange={setDefaultRg3View}
                                options={RG3_VIEW_OPTIONS}
                                placeholder="Sélectionner une option"
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[16, 8]}>
                    <Col span={12}>
                        <Form.Item label="Ms Pro" className="no-box-shadow" style={{ marginBottom: 8 }}>
                            <Switch checked={isMsPro} onChange={setIsMsPro} size="small" />
                        </Form.Item>
                    </Col>
                    <Col span={12}>
                        <CnAdministratorAccess
                            orgaId={props.orgaId}
                            administratorAccessValue={adminAccess}
                            onChange={setAdminAccess}
                        />
                    </Col>
                </Row>

                <Divider style={{ margin: '4px 0 12px' }} />

                <Flex justify="space-between" align="center" wrap="wrap" gap={8}>
                    <Space size="small" wrap>
                        <ModalCustomContactSupport
                            setData={setCustomContactSupport}
                            data={customContactSupport}
                        />
                        {props.softaccessId === 'new' ? null : (
                            <Tooltip title="Supprime tous les logs liés aux actions des collaborateurs de cette organisation.">
                                <Button size="small" danger onClick={() => setIsModalOpen(true)}>
                                    Effacer les logs
                                </Button>
                            </Tooltip>
                        )}
                    </Space>
                </Flex>
            </Form>

            <Modal
                title={<><WarningOutlined style={{ marginRight: 8 }} />Confirmation de suppression des logs</>}
                closable={{ 'aria-label': 'Custom Close Button' }}
                open={isModalOpen}
                onOk={handleOk}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        Annuler
                    </Button>,
                    <Button key="submit" type="primary" danger loading={loading} onClick={handleOk}>
                        Confirmer
                    </Button>,
                ]}
            >
                <p>Êtes-vous sûr de vouloir effacer tous les logs liés à l'organisation ?</p>
                {feedbackMessage && (
                    <Typography.Text>{feedbackMessage}</Typography.Text>
                )}
            </Modal>
        </>
    );
});

export default CnGeneralConfiguration;

function ModalCustomContactSupport({ data, setData }: { data: ContactInfo | null, setData: (contactInfo: ContactInfo | null) => void }) {

    const [name, setName] = useState(data?.name || "");
    const [email, setEmail] = useState(data?.email || "");
    const [phone, setPhone] = useState(data?.phone || "");
    const [contactSupportModalOpen, setContactSupportModalOpen] = useState(false);

    function handleSave() {
        setData({ name, email, phone });
        setContactSupportModalOpen(false);
    }

    function openModal() {
        setName(data?.name || "");
        setEmail(data?.email || "");
        setPhone(data?.phone || "");
        setContactSupportModalOpen(true);
    }

    return <>
        <Space size="small">
            <Button size="small" icon={<UserOutlined />} onClick={openModal}>
                Contact support personnalisé
            </Button>
        </Space>
        <Modal
            title="Contact support personnalisé"
            closable={{ 'aria-label': 'Custom Close Button' }}
            open={contactSupportModalOpen}
            onOk={handleSave}
            onCancel={() => setContactSupportModalOpen(false)}
            footer={[
                <Button key="back" onClick={() => setContactSupportModalOpen(false)}>
                    Annuler
                </Button>,
                <Button key="submit" type="primary" onClick={handleSave}>
                    Ok
                </Button>,
            ]}
        >
            <Flex justify="flex-end" style={{ marginBottom: 12 }}>
                <Button
                    size="small"
                    onClick={() => {
                        setName("");
                        setEmail("");
                        setPhone("");
                        setData(null);
                        setContactSupportModalOpen(false);
                    }}
                >
                    Remettre par défaut
                </Button>
            </Flex>
            <Form layout="vertical" size="small">
                <Form.Item label="Nom" className="no-box-shadow" style={{ marginBottom: 8 }}>
                    <Input
                        placeholder="Nom"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                    />
                </Form.Item>
                <Form.Item label="Email" className="no-box-shadow" style={{ marginBottom: 8 }}>
                    <Input
                        placeholder="Email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                    />
                </Form.Item>
                <Form.Item label="Téléphone" className="no-box-shadow" style={{ marginBottom: 8 }}>
                    <Input
                        placeholder="Téléphone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                    />
                </Form.Item>
            </Form>
        </Modal>
    </>
}
